import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { IKnowledgeItem } from "./types"

import { KnowledgeHubAdminService, SegmentsAdminService } from "src/api"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { reportError } from "src/lib/report"

export class PropertyDetailStore {
    //#region initialization
    static Context = React.createContext<PropertyDetailStore | null>(null)
    static LoadingKeys = createLoadingKeys("loading")
    knowledgeItems = new Pagination(
        async (params) => {
            const response =
                await KnowledgeHubAdminService.postV1AdminKnowledgebaseKnowledgeItemsList(
                    {
                        request: {
                            segment_ids:
                                this._segmentId !== null
                                    ? [this._segmentId]
                                    : [],
                            page_number: params.page,
                            page_size: params.pageSize,
                        },
                    },
                )

            const items: IKnowledgeItem[] = (
                response.knowledge_items ?? []
            ).map((item) => ({
                id: item.id ?? -1,
                title: item.title ?? "",
                description: item.description ?? "",
                segments: item.segments ?? [],
                segment_names: item.segment_names ?? [],
                knowledge_item_type: item.knowledge_item_type ?? undefined,
                access_type: item.access_type ?? "NONE",
            }))

            return {
                items,
                count: response.total_count ?? 0,
            }
        },
        {
            loadingKey: PropertyDetailStore.LoadingKeys.loading,
        },
    )

    constructor() {
        makeAutoObservable(this)
    }

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    _segmentId: number | null = null
    _segmentType: string | null = null
    _segmentName: string | null = null

    private get accessGroupId() {
        return this._accessGroupId
    }

    get segmentId() {
        return this._segmentId
    }

    get segmentType() {
        return this._segmentType
    }

    get segmentName() {
        return this._segmentName
    }

    //#region store operations
    async init(accessGroupId: number, segmentId: number) {
        this.setAccessGroup(accessGroupId)
        this.setSegmentId(segmentId)
        try {
            await this.fetchSegmentDetails(segmentId)
            await this.knowledgeItems.loadInitialPage()
        } catch (error) {
            reportError(t`property-detail.init-fail`, error)
        }
    }

    async fetchSegmentDetails(segmentId: number) {
        try {
            const segment = await SegmentsAdminService.getV1AdminSegment1({
                segmentId,
            })
            this.setSegmentDetails(segment.type ?? null, segment.name ?? null)
        } catch (error) {
            reportError(t`property-detail.segment-fetch-fail`, error)
        }
    }

    async deleteKnowledgeItem(id: number) {
        try {
            await KnowledgeHubAdminService.deleteV1AdminKnowledgebaseKnowledgeItems(
                {
                    knowledgeItemId: id,
                },
            )
        } catch (error) {
            reportError(t`property-detail.delete.failed`, error)
        } finally {
            await this.knowledgeItems.reload()
        }
    }

    //#region setters
    private setAccessGroup(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    private setSegmentId(segmentId: number) {
        this._segmentId = segmentId
    }

    private setSegmentDetails(type: string | null, name: string | null) {
        this._segmentType = type
        this._segmentName = name
    }
    //#endregion
}
